/**
 * OverrideFork — one fork, drawn: where the engineer stood, what the runbook
 * said to do next, and where the engineer actually went instead.
 *
 * WHY NOT TrailGraph. TrailGraph draws a WHOLE trail with dagre and a detail
 * panel; one per row of a ranked list would be a page of heavy, near-identical
 * canvases with the interesting three nodes buried in twenty. A gap row has
 * exactly one question — "which branch was refused, and for what?" — so this
 * draws exactly that: one node, its runbook branches, and the engineer's own
 * destination. Three to five boxes, no layout engine, no interaction.
 *
 * It is NOT a second visual language. Every colour, shape, tint and dash comes
 * from TrailGraph's exports (`typeStyle`, `shapeForNode`, `nodeTypeFromNid`,
 * `TYPE_LABEL`, `tintOf`), so a Decision is the same sky diamond here as it is
 * on the trail graph and the Diagnostic Map, an untaken branch is the same grey
 * dashed ghost, and the engineer's own move keeps the trail graph's amber.
 * TrailGraph itself is imported from, never modified.
 */
import React from "react";
import {
  nodePalette,
  nodeTypeFromNid,
  shapeForNode,
  tintOf,
} from "./TrailGraph";

/* Geometry. One fixed viewBox scaled to the row's width, so a row is the same
   drawing at every breakpoint and nothing has to be measured. */
const VB_W = 660;
const L_W = 236;        // the node the engineer stood on
const R_W = 268;        // a branch / a destination
const GAP_X = 104;      // the connector channel
const PAD = 14;
const ROW_GAP = 14;
const CHAR_W = 6.3;     // TrailGraph's measured Space Grotesk average
const LINE_H = 14;
const CHIP_H = 14;
const MIN_H = 54;
const MAX_LINES = 3;

/* The trail graph's edge palette, for the same three meanings. */
const GHOST_STROKE = "#c2ccd8";
const JUMP_STROKE = "#f59e0b";

/** Greedy wrap into at most `maxLines`, last line ellipsised. Mirrors
    TrailGraph's wrapLabel, which is module-private there. */
function wrap(text, maxChars, maxLines = MAX_LINES) {
  const words = String(text || "").trim().split(/\s+/).filter(Boolean);
  if (!words.length) return [""];
  const lines = [];
  let cur = "";
  for (const w of words) {
    if (!cur) cur = w;
    else if ((`${cur} ${w}`).length <= maxChars) cur += ` ${w}`;
    else { lines.push(cur); cur = w; }
  }
  if (cur) lines.push(cur);
  if (lines.length > maxLines) {
    const kept = lines.slice(0, maxLines);
    kept[maxLines - 1] = `${kept[maxLines - 1].slice(0, maxChars - 1)}…`;
    return kept;
  }
  return lines;
}

/** Text budget per shape — a diamond only offers half its width at the text
    band, exactly as TrailGraph's shapeMetrics accounts for. */
function metrics(shape, W) {
  switch (shape) {
    case "diamond": return { textW: W * 0.52, hMul: 1.75 };
    case "hexagon": return { textW: W - 2 * (Math.min(26, W * 0.12) + 8), hMul: 1.12 };
    case "stadium": return { textW: W - 52, hMul: 1.05 };
    case "roundedRect": return { textW: W - 32, hMul: 1 };
    case "subroutine": return { textW: W - 40, hMul: 1 };
    case "parallelogram":
    case "trapezoid": return { textW: W - 2 * (Math.min(20, W * 0.07) + 12), hMul: 1.06 };
    default: return { textW: W - 28, hMul: 1 };
  }
}

/** The outline. Same shape language as TrailGraph / the Diagnostic Map
    (ShapeOutline there is module-private, so the cases are repeated). */
function Outline({ shape, W, H, fill, stroke, strokeWidth, dash }) {
  const hw = W / 2;
  const hh = H / 2;
  const p = { fill, stroke, strokeWidth, strokeDasharray: dash || undefined };
  switch (shape) {
    case "diamond":
      return <polygon points={`0,${-hh} ${hw},0 0,${hh} ${-hw},0`} {...p} />;
    case "hexagon": {
      const i = Math.min(26, hw * 0.24);
      return (
        <polygon
          points={`${-hw},0 ${-hw + i},${-hh} ${hw - i},${-hh} ${hw},0 ${hw - i},${hh} ${-hw + i},${hh}`}
          {...p}
        />
      );
    }
    case "stadium":
      return <rect x={-hw} y={-hh} width={W} height={H} rx={hh} ry={hh} {...p} />;
    case "roundedRect":
      return <rect x={-hw} y={-hh} width={W} height={H} rx={14} ry={14} {...p} />;
    case "parallelogram": {
      const sk = Math.min(20, hw * 0.14);
      return <polygon points={`${-hw + sk},${-hh} ${hw},${-hh} ${hw - sk},${hh} ${-hw},${hh}`} {...p} />;
    }
    case "trapezoid": {
      const sk = Math.min(20, hw * 0.14);
      return <polygon points={`${-hw + sk},${-hh} ${hw - sk},${-hh} ${hw},${hh} ${-hw},${hh}`} {...p} />;
    }
    case "subroutine": {
      const ins = Math.min(12, hw * 0.1);
      return (
        <>
          <rect x={-hw} y={-hh} width={W} height={H} rx={4} ry={4} {...p} />
          <line x1={-hw + ins} y1={-hh} x2={-hw + ins} y2={hh} stroke={stroke} strokeWidth={1} />
          <line x1={hw - ins} y1={-hh} x2={hw - ins} y2={hh} stroke={stroke} strokeWidth={1} />
        </>
      );
    }
    default:
      return <rect x={-hw} y={-hh} width={W} height={H} rx={8} ry={8} {...p} />;
  }
}

/* A box is prepared before it is drawn: the label wrap decides the height, and
   the heights decide the canvas. */
function prepare(node, W, { ghost = false, chip = "" } = {}) {
  const type =
    node.node_type || nodeTypeFromNid(node.nid, node.title || "") || "DiagnosticStep";
  const shape = shapeForNode({ type });
  const { textW, hMul } = metrics(shape, W);
  const maxChars = Math.max(14, Math.floor(textW / CHAR_W));
  // No title means the graph could not name the nid: the nid IS the label then,
  // rather than an empty box with a caption nobody can read.
  const label = node.title || node.nid || "Unknown node";
  const lines = wrap(label, maxChars, MAX_LINES);
  const nidLine = node.title && node.nid ? CHIP_H : 0;
  const chipLine = chip ? CHIP_H : 0;
  const content = lines.length * LINE_H + nidLine + chipLine + 20;
  const H = Math.round(Math.max(MIN_H, content * hMul));
  // `nodePalette` is TrailGraph's own "a branch nobody took has no claim on
  // hue" rule: family colour when walked, the neutral grey when a ghost.
  const style = nodePalette({ type, kind: ghost ? "sibling" : undefined });
  return { ...node, type, shape, lines, chip, nid: node.nid, W, H, style, ghost, textW };
}

function Box({ box, x, y, accent }) {
  const { W, H, shape, lines, style, ghost, chip } = box;
  const stroke = accent || style.border;
  const showNid = Boolean(box.title && box.nid);
  /* The content block is CENTRED, not top-aligned: a diamond is drawn far
     taller than its text so the text fits between its sloping sides, and a
     top-aligned caption would sit outside the outline entirely. */
  const contentH =
    (chip ? CHIP_H : 0) + lines.length * LINE_H + (showNid ? CHIP_H : 0);
  const y0 = -contentH / 2;
  const textTop = y0 + (chip ? CHIP_H : 0);
  return (
    <g transform={`translate(${x},${y})`}>
      <Outline
        shape={shape}
        W={W}
        H={H}
        fill={ghost ? "#f8fafc" : tintOf(style.bg, 0.2)}
        stroke={stroke}
        strokeWidth={ghost ? 1.3 : 1.7}
        dash={ghost ? "4 4" : undefined}
      />
      {chip ? (
        <text
          x={0}
          y={y0 + 10}
          textAnchor="middle"
          fontSize={8.5}
          letterSpacing="0.06em"
          fill={accent || "#64748b"}
          fontWeight={700}
        >
          {chip}
        </text>
      ) : null}
      {lines.map((ln, i) => (
        <text
          key={i}
          x={0}
          y={textTop + 11 + i * LINE_H}
          textAnchor="middle"
          fontSize={11.5}
          fontStyle={ghost ? "italic" : undefined}
          fill={ghost ? "#64748b" : style.text}
        >
          {ln}
        </text>
      ))}
      {showNid ? (
        <text
          x={0}
          y={textTop + 11 + lines.length * LINE_H + 1}
          textAnchor="middle"
          fontSize={8.5}
          fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
          fill="#94a3b8"
        >
          {box.nid}
        </text>
      ) : null}
    </g>
  );
}

/** A bordered pill on the connector — the Diagnostic Map's edge-label idiom. */
function EdgePill({ x, y, text, color }) {
  const w = Math.max(38, text.length * 5.3 + 12);
  return (
    <g transform={`translate(${x},${y})`}>
      <rect
        x={-w / 2} y={-8} width={w} height={16} rx={8}
        fill="rgba(255,255,255,0.96)" stroke={color} strokeWidth={0.9}
      />
      <text x={0} y={3.5} textAnchor="middle" fontSize={8.5} fill={color} fontWeight={600}>
        {text}
      </text>
    </g>
  );
}

/**
 * @param {object}   node        {nid, title, node_type} — where they stood
 * @param {Array}    suggested   runbook branches out of that node (not taken)
 * @param {Array}    destinations where engineers actually went (0..2)
 * @param {boolean}  unknownDestination  no destination could be established
 */
export default function OverrideFork({
  node,
  suggested = [],
  destinations = [],
  unknownDestination = false,
}) {
  if (!node) return null;

  const left = prepare(node, L_W, { chip: "THIS STEP" });

  const rights = [
    ...suggested.map((s) => ({
      kind: "suggested",
      box: prepare(s, R_W, { ghost: !s.taken, chip: s.taken ? "ALSO REACHED" : "NOT TAKEN" }),
      pill: s.condition ? `runbook: ${s.condition}` : "runbook says next",
      color: s.taken ? "#94a3b8" : GHOST_STROKE,
    })),
    ...destinations.map((d) => ({
      kind: "went",
      box: prepare(d, R_W, {
        chip: d.origin === "derived" ? "WENT HERE (from the next step)" : "WENT HERE",
      }),
      pill: "engineer went",
      color: JUMP_STROKE,
      accent: JUMP_STROKE,
    })),
  ];

  if (!rights.length || unknownDestination) {
    rights.push({
      kind: "unknown",
      box: {
        ...prepare(
          { nid: "", title: "Trail ends here" },
          R_W,
          { chip: "NOT CAPTURED" },
        ),
        ghost: true,
      },
      pill: "engineer went",
      color: JUMP_STROKE,
      accent: JUMP_STROKE,
    });
  }

  const rightH = rights.reduce((s, r) => s + r.box.H, 0) + ROW_GAP * (rights.length - 1);
  const H = Math.round(Math.max(left.H, rightH) + PAD * 2);
  const lx = PAD + L_W / 2;
  const rx = PAD + L_W + GAP_X + R_W / 2;
  const cy = H / 2;

  let cursor = (H - rightH) / 2;
  const placed = rights.map((r) => {
    const y = cursor + r.box.H / 2;
    cursor += r.box.H + ROW_GAP;
    return { ...r, y };
  });

  const ax = PAD + L_W;        // connector start (left box right edge)
  const bx = PAD + L_W + GAP_X; // connector end (right box left edge)
  const mx = (ax + bx) / 2;

  return (
    <svg
      viewBox={`0 0 ${VB_W} ${H}`}
      width="100%"
      role="img"
      aria-label={`${node.title || node.nid}: runbook branches versus where the engineer went`}
      style={{ maxWidth: VB_W, display: "block" }}
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        <marker id="of-arrow-ghost" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
          <path d="M0,0 L8,4 L0,8 z" fill={GHOST_STROKE} />
        </marker>
        <marker id="of-arrow-jump" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
          <path d="M0,0 L8,4 L0,8 z" fill={JUMP_STROKE} />
        </marker>
      </defs>

      {placed.map((r, i) => (
        <path
          key={`e${i}`}
          d={`M${ax},${cy} C${mx},${cy} ${mx},${r.y} ${bx - 2},${r.y}`}
          fill="none"
          stroke={r.color}
          strokeWidth={r.kind === "suggested" ? 1.3 : 1.6}
          strokeDasharray={r.kind === "suggested" ? "4 4" : "5 4"}
          markerEnd={`url(#${r.kind === "suggested" ? "of-arrow-ghost" : "of-arrow-jump"})`}
        />
      ))}
      {placed.map((r, i) => (
        <EdgePill
          key={`p${i}`}
          x={mx}
          /* 3/4 of the way to its own target, not halfway: pills then separate
             as far as the boxes do, so five branches cannot stack up in the
             middle of the channel. */
          y={cy + (r.y - cy) * 0.75}
          text={r.pill}
          color={r.color}
        />
      ))}

      <Box box={left} x={lx} y={cy} />
      {placed.map((r, i) => (
        <Box key={`b${i}`} box={r.box} x={rx} y={r.y} accent={r.accent} />
      ))}
    </svg>
  );
}
